/**
 * Drag-drop safety net (layer 2).
 *
 * FullCalendar binds an in-progress drag to a specific DOM element. If the
 * controlled `events` array (or another option) is reprocessed mid-drag, that
 * element is torn down and the drag is silently aborted: on pointer-up
 * FullCalendar has no element to read a drop position from, so it fires NO
 * `eventChange`/`eventDrop` and the appointment snaps back to its origin — the
 * move is lost without any error.
 *
 * The freeze of the `events` array (layer 1) prevents most of these aborts, but
 * other prop churn can still trigger one. This module is the catch layer: when a
 * drag ends without FullCalendar delivering the move, we recover the user's
 * intended target straight from the DOM by hit-testing the pointer-up
 * coordinates against the time-grid, completely independent of FullCalendar's
 * React lifecycle (the same DOM-overlay technique used by useTimeSlotIndicator /
 * CellNavigation).
 *
 * The result is then replayed through FullCalendar's own API (setDates /
 * setResources), which fires a normal `eventChange` and re-enters the regular
 * reschedule pipeline (validation, DTO build, optimistic update, mutation).
 */

export interface DropTarget {
  /** Slot time at the drop position, in `HH:mm:ss` form (data-time attribute). */
  time: string;
  /** Resource (therapist column) id under the drop, or null if none resolved. */
  resourceId: string | null;
}

/**
 * Hit-test a pointer position against the FullCalendar time-grid to determine
 * which slot (time) and resource column the pointer was over. Returns null when
 * the position is not over a grid slot (e.g. dropped outside the calendar, or
 * the gesture was cancelled away from the grid).
 *
 * Uses the same selectors CellNavigation relies on:
 *   - `.fc-timegrid-slot-resource[data-time]` / `.fc-timegrid-slot[data-time]`
 *   - the nearest `[data-resource-id]` ancestor for the column.
 */
export const hitTestDropTarget = (
  root: HTMLElement,
  clientX: number,
  clientY: number,
): DropTarget | null => {
  const stack = document.elementsFromPoint(clientX, clientY) as HTMLElement[];

  let slotEl: HTMLElement | null = null;
  let resourceId: string | null = null;

  for (const el of stack) {
    if (!root.contains(el)) continue;

    if (!slotEl) {
      const slot = el.closest(
        ".fc-timegrid-slot-resource[data-time], .fc-timegrid-slot[data-time]",
      ) as HTMLElement | null;
      if (slot) slotEl = slot;
    }

    if (!resourceId) {
      const resCell = el.closest("[data-resource-id]") as HTMLElement | null;
      if (resCell) resourceId = resCell.getAttribute("data-resource-id");
    }

    if (slotEl && resourceId) break;
  }

  const time = slotEl?.getAttribute("data-time") ?? null;
  if (!time) return null;

  return { time, resourceId };
};
